import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import BDImage from '@shared/components/elements/bd-image';
import {
  CLARITY_OPTIONS,
  COLOR_OPTIONS,
  CUT_OPTIONS,
  DIAMOND_IMAGE_SIZES,
} from '@shared/lib/constants';
import { DiamondVariant } from '@shared/lib/shopify/types/shopify-native';
import { getCaratTitle } from '@shared/lib/utils/product-utils';
import clsx from 'clsx';

import colorDImage from '@shared/public/images/color/Color_D.png';
import colorEImage from '@shared/public/images/color/Color_E.png';
import colorFImage from '@shared/public/images/color/Color_F.png';
import colorGImage from '@shared/public/images/color/Color_G.jpeg';
import colorHImage from '@shared/public/images/color/Color_H.jpeg';
import colorIImage from '@shared/public/images/color/Color_I.jpeg';
import colorJImage from '@shared/public/images/color/Color_J.jpeg';
import Image from 'next/image';
const colorImages = [
  colorJImage,
  colorIImage,
  colorHImage,
  colorGImage,
  colorFImage,
  colorEImage,
  colorDImage,
];

export default function Diamond4Cs({
  sizeVariants,
  selectedVariant,
}: {
  sizeVariants: DiamondVariant[];
  selectedVariant?: DiamondVariant;
}) {
  return (
    <>
      {selectedVariant && (
        <div key="carat" className="flex flex-col gap-4">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-normal font-normal">Carat</h4>
            <Popover className="relative">
              <PopoverButton className="flex items-center justify-center">
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </PopoverButton>
              <PopoverPanel className="absolute left-7 top-0 z-10 w-80 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
                <p className="font-semibold">Carat (ct.) </p>
                <p className="">
                  The international unit of weight, used for measuring diamonds and gemstones. 1
                  carat is equal to 200 milligrams, or 0.2 grams.
                </p>
              </PopoverPanel>
            </Popover>
          </div>
          <div id="variantId" className="grid grid-cols-7">
            {sizeVariants.map((variant, index) => (
              <div
                key={variant.id}
                className={clsx(
                  'border-bd-contrast relative mb-6 flex flex-col items-center justify-end gap-1 border-y border-r bg-white py-4 first:rounded-l-xl first:border-l last:rounded-r-xl',
                  variant.id === selectedVariant?.id && 'border-bd-primary !bg-bd-contrast border-l'
                )}
              >
                <Image
                  src={variant.image.url}
                  alt={variant.image.altText || variant.title}
                  width={variant.image.width}
                  height={variant.image.height}
                  style={{
                    width: `${DIAMOND_IMAGE_SIZES[index]!}em`,
                    height: `${DIAMOND_IMAGE_SIZES[index]!}em`,
                  }}
                  className="object-cover object-center"
                />
                <p className="whitespace-nowrap text-xs md:text-sm">
                  {getCaratTitle(variant.selectedOptions || [])}
                </p>
                {variant.id === selectedVariant?.id && (
                  <p className="text-bd-primary absolute left-1/2 top-full col-start-5 col-end-9 mt-4 -translate-x-1/2 whitespace-nowrap text-center text-xs">
                    At least{' '}
                    {variant.selectedOptions
                      ?.find((option) => option.name === 'Size')!
                      .value.slice(0, -5)}
                    carat.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div key="color" className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-normal">Color</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-96 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Diamond Color</p>
              <p className="">
                The color of a diamond is determined by the presence of trace elements in the
                diamond's crystal structure, with the D being the most colorless. F, E and D are
                "colorless" grade appreciated best set in platinum or white gold. I, H are
                "near-colorless" grade, excellent value set in yellow or rose gold.
              </p>
            </PopoverPanel>
          </Popover>
        </div>
        <p className="text-sm">
          We guarantee your Bonded Diamond™ will be better than F color, which is considered
          as"colorless". You may not make a selection here.
        </p>
        <div className="grid grid-cols-7">
          {COLOR_OPTIONS.map((clarity, index) => (
            <div
              key={clarity}
              className={clsx(
                'border-bd-contrast relative flex flex-col items-center justify-end gap-1 border-y border-r bg-white py-4 first:rounded-l-xl first:border-l last:rounded-r-xl',
                index >= 4 && 'border-bd-primary !bg-bd-contrast',
                index === 4 && 'border'
              )}
            >
              <div className="md:w-18 relative h-10 w-12 md:h-12">
                <BDImage
                  src={colorImages[index]!}
                  alt={`${clarity} color`}
                  className="scale-y-125 object-cover object-center"
                />
              </div>
              <p className="text-sm text-slate-800">{clarity}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          <p className="text-bd-primary col-start-5 col-end-8 text-center text-xs">
            F color and better guaranteed.
          </p>
        </div>
      </div>
      <div key="clarity" className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-normal">Clarity</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-96 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Diamond Color</p>
              <p className="">
                The clarity of a diamond is determined by the presence of internal and external
                characteristics, with the FL being the flawless grade. VVS2, VVS1 are very, very
                slightly included grades. Characteristics miniscule and difficult to see under 10x
                magnification, even to a trained eye.
              </p>
            </PopoverPanel>
          </Popover>
        </div>
        <p className="text-sm">
          We guarantee your Bonded Diamond™ will be better than VVS2 clarity, which is considered
          as "very, very slightly included". You may not make a selection here.
        </p>
        <div className="grid grid-cols-8">
          {CLARITY_OPTIONS.map((clarity, index) => (
            <div
              key={clarity}
              className={clsx(
                'border-bd-contrast relative flex flex-col items-center justify-end gap-1 border-y border-r bg-white py-4 first:rounded-l-xl first:border-l last:rounded-r-xl',
                index >= 4 && 'border-bd-primary !bg-bd-contrast',
                index === 4 && 'border'
              )}
            >
              <p className="text-sm text-slate-800">{clarity}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-8">
          <p className="text-bd-primary col-start-5 col-end-9 text-center text-xs">
            VVS2 clarity and better guaranteed.
          </p>
        </div>
      </div>

      <div key="cut" className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-normal">Cut</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-96 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Diamond Color</p>
              <p className="">
                The cut represents the quality of a diamond's proportions, symmetry, and polish. The
                ideal cut is the most symmetrical and well-proportioned diamond, resulting in
                maximum sparkle and fire.
              </p>
            </PopoverPanel>
          </Popover>
        </div>

        <p className="text-sm">
          We works with the best jeweler to achieve the best cut, IGI graded Ideal cut or above is
          guaranteed. You may not make a selection here.
        </p>
        <div className="grid grid-cols-5">
          {CUT_OPTIONS.map((clarity, index) => (
            <div
              key={clarity}
              className={clsx(
                'border-bd-contrast relative flex flex-col items-center justify-end gap-1 border-y border-r bg-white py-4 first:rounded-l-xl first:border-l last:rounded-r-xl',
                index >= 3 && 'border-bd-primary !bg-bd-contrast',
                index === 3 && 'border'
              )}
            >
              <p className="text-xs text-slate-800 sm:text-sm">{clarity}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          <p className="text-bd-primary col-start-5 col-end-8 text-center text-xs">
            Excellent cut or better guaranteed.
          </p>
        </div>
      </div>
    </>
  );
}
